import { forwardRef, useId, type ReactNode } from 'react';
import { SocialPlatform } from '@mg/shared';
import { cn } from '../../lib/cn';

/**
 * Two small controls the editor's panels and the cards of a thread both draw. They live here, not
 * in ContentBlocksEditor.tsx, because the thread components that the editor renders draw them too:
 * a shared module keeps the imports one way.
 */

/**
 * An icon-only button. Its label is its accessible name and its tooltip. Forwards its ref, so the
 * editor can put focus back on it after a move.
 */
export const IconButton = forwardRef<
  HTMLButtonElement,
  {
    label: string;
    onClick: () => void;
    disabled?: boolean;
    danger?: boolean;
    children: ReactNode;
  }
>(function IconButton({ label, onClick, disabled, danger, children }, ref) {
  return (
    <button
      ref={ref}
      type="button"
      aria-label={label}
      title={label}
      disabled={disabled}
      onClick={onClick}
      className={cn(
        'focus-ring rounded-md p-1.5 disabled:cursor-not-allowed disabled:opacity-30',
        danger
          ? 'text-ink-faint hover:bg-red-50 dark:hover:bg-error-dark-tint hover:text-red-600 dark:hover:text-error-dark'
          : 'text-ink-faint hover:bg-chip hover:text-ink-strong',
      )}
    >
      {children}
    </button>
  );
});

/**
 * Why a platform's count is not the number of characters typed. The counter shows the platform's own
 * number (`countPostChars`), so a post with a link or an emoji in it reads differently from what was
 * typed; this says why, on the two platforms that count their own way.
 */
const COUNT_HINT: Partial<Record<SocialPlatform, string>> = {
  [SocialPlatform.X]:
    'X counts every link as 23 characters, and emoji and Chinese, Japanese and Korean characters as 2.',
  [SocialPlatform.THREADS]: 'Threads counts each emoji as several characters.',
};

/**
 * A text's length against the destination's character cap, "count / limit". Over the cap the count
 * goes red, and the composer blocks Save and Publish: the platform would refuse the post anyway.
 *
 * `length` is the count as the platform makes it. Where that is not the number of characters typed
 * (`platform`: X, Threads) the counter says why: as its tooltip, and as its description for a
 * screen reader.
 */
export function CharCounter({
  length,
  limit,
  id,
  className,
  platform = null,
}: {
  length: number;
  limit: number;
  /** For a text box that names the counter as its description while it is over. */
  id?: string;
  className?: string;
  /** The destination the count is made for. */
  platform?: SocialPlatform | null;
}) {
  const hintId = useId();
  const hint = platform ? COUNT_HINT[platform] : undefined;
  return (
    <>
      <span
        id={id}
        title={hint}
        aria-describedby={hint ? hintId : undefined}
        className={cn('text-xs tabular-nums text-ink-faint', className)}
      >
        <span className={cn(length > limit && 'font-semibold text-red-600 dark:text-error-dark')}>
          {length.toLocaleString()}
        </span>{' '}
        / {limit.toLocaleString()}
      </span>
      {hint && (
        <span id={hintId} className="sr-only">
          {hint}
        </span>
      )}
    </>
  );
}
